import { all, takeLatest } from 'redux-saga/effects'
import { debounce, isEmpty, isEqual, kebabCase } from 'lodash'
import { produce } from 'immer'
import labelApi, {
  LabelData,
  LabelReadByMarkupRequest
} from '../../apis/label.api'
import alertService from '../../services/alert.service'
import store from '../../store'
import {
  actions,
  resetId,
  resetActive,
  replaceId,
  replaceMarkup,
  replaceActive,
  resetSelectedElement,
  resetElements,
  resetStage,
  recordHistory,
  replaceElements,
  resetHistory
} from './LabelBuilder.slice'
import unitUtil from '../../utils/unit.util'

function resetFields () {
  const { labelBuilder } = store.getState()
  if (labelBuilder.id) {
    store.dispatch(resetId())
    store.dispatch(resetActive())
    store.dispatch(resetSelectedElement())
    store.dispatch(resetElements())
    store.dispatch(resetStage())
  }
}
function readLabelSize () {
  const { labelBuilder: label } = store.getState()
  const { type: labelType } = label.markup
  if (!labelType) return
  const payload = { type: labelType }
  labelApi.size(payload, size => {
    if (size) {
      const { width, height, unit, type } = size
      const canAlert = () => {
        const { stage } = label.markup
        const isSameType = type && type === label.markup.type
        const stageSize = {
          width: unitUtil.unitToPixel(stage.unit, stage.width),
          height: unitUtil.unitToPixel(stage.unit, stage.height)
        }
        const activeStageSize = {
          width: unitUtil.unitToPixel(unit, width),
          height: unitUtil.unitToPixel(unit, height)
        }
        const isSameSize = isEqual(stageSize, activeStageSize)
        return !isSameType && !isSameSize
      }
      if (canAlert()) {
        const typeName = kebabCase(type)
        const message = `The label size must match the ${typeName} label size (${width}x${height} inch)`
        alertService.info(message)
      }
      const markup = produce(label.markup, markup => {
        markup.stage.width = width
        markup.stage.height = height
        markup.stage.unit = unit
      })
      store.dispatch(replaceMarkup(markup))
    }
  })
}

function replaceLabel (label: LabelData) {
  const { id, markup, active } = label
  store.dispatch(replaceId(id))
  store.dispatch(replaceMarkup(markup))
  store.dispatch(replaceActive(active))
}

function readLabel () {
  const { labelBuilder } = store.getState()
  const { markup } = labelBuilder
  const payload: LabelReadByMarkupRequest = { markup }
  labelApi.readByMarkup(payload, label => {
    resetFields()
    if (label && !isEmpty(label)) {
      replaceLabel(label)
    }
    readLabelSize()
  })
}

const recordElementsToHistory = debounce(() => {
  const { labelBuilder: { markup } } = store.getState()
  store.dispatch(recordHistory(markup.elements))
}, 500)

function repalceElementsWithHistoryPointerElements () {
  const { labelBuilder: { history: { stack, pointer } } } = store.getState()
  const pointerElements = pointer === null ? null : stack[pointer]
  if (!pointerElements) return
  store.dispatch(replaceElements(pointerElements))
}

function handleReplaceMarkup () {
  recordElementsToHistory()
  store.dispatch(resetHistory())
}

export default function * effects () {
  yield all([
    takeLatest(actions.replaceMarkupType, readLabel),
    takeLatest(actions.replaceMarkupCustomLabelId, readLabel),
    takeLatest(actions.resetMarkupType, resetFields),
    takeLatest(actions.replaceMarkup, handleReplaceMarkup),
    takeLatest(actions.addBarcodeElement, recordElementsToHistory),
    takeLatest(actions.addImageElement, recordElementsToHistory),
    takeLatest(actions.addLineElement, recordElementsToHistory),
    takeLatest(actions.addCircleElement, recordElementsToHistory),

    takeLatest(actions.addRectElement, recordElementsToHistory),
    takeLatest(actions.addEllipseElement, recordElementsToHistory),
    takeLatest(actions.addWedgeElement, recordElementsToHistory),
    takeLatest(actions.addRingElement, recordElementsToHistory),
    takeLatest(actions.addArcElement, recordElementsToHistory),
    takeLatest(actions.addArrowElement, recordElementsToHistory),

    takeLatest(actions.addTextElement, recordElementsToHistory),
    takeLatest(actions.transformElement, recordElementsToHistory),
    takeLatest(actions.dragElement, recordElementsToHistory),
    takeLatest(actions.replaceImageElementImage, recordElementsToHistory),
    takeLatest(actions.replaceElementText, recordElementsToHistory),
    takeLatest(actions.appendElementText, recordElementsToHistory),
    takeLatest(actions.replaceTextElementFontFamily, recordElementsToHistory),
    takeLatest(actions.replaceTextElementFontSize, recordElementsToHistory),
    takeLatest(actions.replaceTextElementFontStyle, recordElementsToHistory),
    takeLatest(actions.replaceTextElementColor, recordElementsToHistory),
    takeLatest(actions.replaceBarcodeElementImage, recordElementsToHistory),
    takeLatest(actions.replaceLineElementStroke, recordElementsToHistory),
    takeLatest(actions.undoHistory, repalceElementsWithHistoryPointerElements),
    takeLatest(actions.redoHistory, repalceElementsWithHistoryPointerElements)
  ])
}
